import { fetchJSON, authFetch } from '@/utils/http'
import { API_ROUTE } from '../backend/config'
import { OTPStatusSchema, SignupData, UserPublicFlag } from './types'
import { DepartmentPermissionsIds } from '../departments/permissions'

// PAYLOAD AUTH ENDPOINTS
// Errors are not controlled by us to we disable the error toast

export const login = async (email: string, password: string) => {
  return await fetchJSON(`${API_ROUTE}/users/login`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    disableErrorToast: true,
  })
}

export const logout = async () => {
  return await fetchJSON(`${API_ROUTE}/users/logout`, {
    method: 'POST',
    disableErrorToast: true,
  })
}

export const signup = async (signup: SignupData) => {
  return await fetchJSON(`${API_ROUTE}/users/signup`, {
    method: 'POST',
    body: JSON.stringify(signup),
    disableErrorToast: true,
  })
}

export const checkAuth = async () => {
  return await fetchJSON(`${API_ROUTE}/users/me`, {
    disableErrorToast: true,
  })
}

export const forgotPassword = async (email: string) => {
  return await fetchJSON(`${API_ROUTE}/users/forgot-password`, {
    method: 'POST',
    body: JSON.stringify({ email }),
    disableErrorToast: true,
  })
}

export const resetPassword = async (token: string, password: string) => {
  return await fetchJSON(`${API_ROUTE}/users/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ token, password }),
    disableErrorToast: true,
  })
}

export const verify = async (token: string) => {
  return await fetchJSON(`${API_ROUTE}/users/verify/${token}`, {
    method: 'POST',
    disableErrorToast: true,
  })
}

// CUSTOM AUTH ENDPOINTS

export const getPrefilledUserData = async (token: string) => {
  return await fetchJSON(`${API_ROUTE}/users/prefilled/${token}`, {})
}

export const checkOTPRequirement = async () => {
  const response = await fetchJSON(`${API_ROUTE}/users/otp`, {
    method: 'GET',
  })

  return OTPStatusSchema.parse(response.status)
}

export const requestOTPCode = async () => {
  const response = await fetchJSON(`${API_ROUTE}/users/otp`, {
    method: 'POST',
  })

  return OTPStatusSchema.parse(response.status)
}

export const verifyOTPCode = async (otp: string) => {
  const response = await fetchJSON(`${API_ROUTE}/users/otp/verify`, {
    method: 'POST',
    body: JSON.stringify({ otp }),
  })

  return OTPStatusSchema.parse(response.status)
}

export const updateUserPublicFlags = async (flags: UserPublicFlag[]) => {
  return await fetchJSON(`${API_ROUTE}/users/public-flags`, {
    method: 'PATCH',
    body: JSON.stringify(flags),
  })
}

export interface UserPermissionsResponse {
  allPermissions: (DepartmentPermissionsIds | string)[]
}

export const fetchUserPermissions = async () => {
  return (await fetchJSON(
    `${API_ROUTE}/users/permissions`,
  )) as UserPermissionsResponse
}

export const updateUserProfile = async (data: {
  name: string
  surname: string
  jobTitle: string
  department: string
  avatar?: File
}) => {
  const formData = new FormData()

  formData.append('name', data.name)
  formData.append('surname', data.surname)
  formData.append('jobTitle', data.jobTitle)
  formData.append('department', data.department)

  if (data.avatar) {
    formData.append('avatar', data.avatar)
  }

  const response = await authFetch(`${API_ROUTE}/users/profile`, {
    method: 'PATCH',
    body: formData,
  })

  return await response.json()
}

export const changePassword = async (
  currentPassword: string,
  newPassword: string,
) => {
  return await fetchJSON(`${API_ROUTE}/users/change-password`, {
    method: 'POST',
    body: JSON.stringify({
      currentPassword,
      newPassword,
    }),
    disableErrorToast: true,
  })
}

const GOOGLE_REDIRECT_URL = `${window.location.origin}/auth/google-callback`

export type GoogleOAuthUrlResponse =
  | { authorized: true }
  | { authorized: false; url: string }
  | { authorized: true; accessToken: string }

export const checkBasicGoogleOAuthStatus = async () => {
  const response = await fetchJSON(
    `${API_ROUTE}/users/google-oauth/start?scope=basic&redirectUrl=${GOOGLE_REDIRECT_URL}`,
  )

  return response as GoogleOAuthUrlResponse
}

export const checkFullGoogleOAuthStatus = async () => {
  const response = await fetchJSON(
    `${API_ROUTE}/users/google-oauth/start?scope=full&redirectUrl=${GOOGLE_REDIRECT_URL}`,
  )

  return response as GoogleOAuthUrlResponse
}

export const checkWorkspaceGoogleOAuthStatus = async () => {
  const response = await fetchJSON(
    `${API_ROUTE}/users/google-oauth/start?scope=workspace&redirectUrl=${GOOGLE_REDIRECT_URL}`,
  )

  return response as GoogleOAuthUrlResponse
}

export interface GoogleUserInfo {
  email: string
  name: string
  picture: string
  verified_email: boolean
}

export interface GoogleConnectionStatus {
  connected: boolean
  scopeLevel: 'none' | 'basic' | 'full' | 'workspace'
  scopes: string[]
  userInfo?: GoogleUserInfo
}

export const getGoogleConnectionStatus = async () => {
  const response = await fetchJSON(`${API_ROUTE}/users/google-oauth/status`)
  return response as GoogleConnectionStatus
}

export const disconnectGoogle = async () => {
  return await fetchJSON(`${API_ROUTE}/users/google-oauth/disconnect`, {
    method: 'POST',
  })
}

export const exchangeGoogleOAuthCredentials = async (code: string) => {
  await fetchJSON(`${API_ROUTE}/users/google-oauth/callback`, {
    method: 'POST',
    body: JSON.stringify({ code, redirectUrl: GOOGLE_REDIRECT_URL }),
  })
}

export const fetchGoogleAccessToken = async (): Promise<string> => {
  try {
    const data = await fetchJSON(
      `${API_ROUTE}/users/google-oauth/access-token`,
      {
        credentials: 'include',
      },
    )

    if (data.accessToken) {
      return data.accessToken
    } else {
      throw new Error('No access token received from backend.')
    }
  } catch (err: any) {
    console.error('Error fetching access token:', err)
    throw new Error(
      err.message || 'Failed to get access token for Google Drive.',
    )
  }
}

// MICROSOFT OAUTH

const MICROSOFT_REDIRECT_URL = `${window.location.origin}/auth/microsoft-callback`

export type MicrosoftOAuthUrlResponse =
  | { authorized: true }
  | { authorized: false; url: string }
  | { authorized: true; accessToken: string }

export const checkBasicMicrosoftOAuthStatus = async () => {
  const response = await fetchJSON(
    `${API_ROUTE}/users/microsoft-oauth/start?scope=basic&redirectUrl=${MICROSOFT_REDIRECT_URL}`,
  )

  return response as MicrosoftOAuthUrlResponse
}

export const checkWorkspaceMicrosoftOAuthStatus = async () => {
  const response = await fetchJSON(
    `${API_ROUTE}/users/microsoft-oauth/start?scope=workspace&redirectUrl=${MICROSOFT_REDIRECT_URL}`,
  )

  return response as MicrosoftOAuthUrlResponse
}

export interface MicrosoftUserInfo {
  email: string
  name: string
  picture: string | null
}

export interface MicrosoftConnectionStatus {
  connected: boolean
  scopeLevel: 'none' | 'basic' | 'workspace'
  scopes: string[]
  userInfo?: MicrosoftUserInfo
}

export const getMicrosoftConnectionStatus = async () => {
  const response = await fetchJSON(`${API_ROUTE}/users/microsoft-oauth/status`)
  return response as MicrosoftConnectionStatus
}

export const disconnectMicrosoft = async () => {
  return await fetchJSON(`${API_ROUTE}/users/microsoft-oauth/disconnect`, {
    method: 'POST',
  })
}

export const exchangeMicrosoftOAuthCredentials = async (code: string) => {
  await fetchJSON(`${API_ROUTE}/users/microsoft-oauth/callback`, {
    method: 'POST',
    body: JSON.stringify({ code, redirectUrl: MICROSOFT_REDIRECT_URL }),
  })
}
